import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Plus } from '@gravity-ui/icons';
import NumberedPagination from '../../../components/NumberedPagination/NumberedPagination';
import { ResizableColgroup, ResizableTh } from '../../../components/ColumnResizeHandle/ColumnResizeHandle';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import CameraHierarchyFilter, { EMPTY_HIERARCHY } from '../../../components/CameraHierarchyFilter/CameraHierarchyFilter';
import ViolationStateBlock from './ViolationStateBlock';
import ViolationRuleViewDrawer from './ViolationRuleViewDrawer';
import ViolationRuleFormDrawer from './ViolationRuleFormDrawer';
import ConfirmRuleDeleteModal from './ConfirmRuleDeleteModal';
import RuleStatusBadge from './RuleStatusBadge';
import RuleOriginBadge from './RuleOriginBadge';
import RulePolicyBadges from './RulePolicyBadges';
import {
  activateViolationRule,
  deactivateViolationRule,
  deleteViolationRule,
  getViolationRuleV2,
  listViolationRulesV2,
  useRuleCategoriesSupported,
} from '../../../services/violations';
import {
  RULE_CATEGORY_BUSINESS,
  RULE_CATEGORY_OPTIONS,
  ruleCategoryLabel,
  ruleCategoryOf,
} from '../../../utils/businessMetrics';
import { describeUserError } from '../../../utils/apiError';
import { describeConflict } from '../../../utils/conflictMessages';
import { hierarchyParams } from '../../../utils/violationScope';
import { formatRuleCondition } from '../../../utils/ruleFormat';
import { formatRecurrenceCompact } from '../../../utils/ruleRecurrence';
import { formatCooldownShort } from '../../../utils/ruleCooldown';
import { useUrlState, useAppliedFilters, HIERARCHY_URL_SCHEMA, pickHierarchy } from '../../../hooks/useUrlState';
import { useCameraNames } from '../../../hooks/useCameraNames';
import { useZonesByCamera } from '../../../hooks/useZonesByCamera';
import { useRuleCatalogs } from '../../../hooks/useRuleCatalog';
import { useRuleBoosts } from '../../../hooks/useRuleBoosts';
import { useAutoDismiss } from '../../../hooks/useAutoDismiss';
import { useRuleActivationModes } from '../../../hooks/useRuleActivationModes';
import {
  DEFAULT_PAGE_SIZE,
  ENABLED_FILTER_OPTIONS,
  PAGE_SIZE_OPTIONS,
  RULE_STATE,
  deriveRuleState,
} from '../../../constants/violationConstants';
import {
  ZONE_UNAVAILABLE_MESSAGE,
  getZoneScopeLabel,
  isZoneUnavailableError,
} from '../../../utils/ruleZones';

const EMPTY_FILTERS = { hierarchy: EMPTY_HIERARCHY, enabled: '', category: '', includeDeleted: false };

// Фильтр категории. Пустое значение — все правила: параметр не отправляется
// (enum violation|business, category=all backend отклоняет с 422).
const CATEGORY_FILTER_OPTIONS = [{ value: '', label: 'Все категории' }, ...RULE_CATEGORY_OPTIONS];

// FE-FILTER02: схема — стабильная ссылка на уровне модуля.
const RULES_URL_SCHEMA = {
  ...HIERARCHY_URL_SCHEMA,
  enabled: { param: 'enabled', default: '' },
  category: { param: 'category', default: '' },
  includeDeleted: { param: 'deleted', default: false, type: 'boolean' },
  limit: { param: 'limit', default: DEFAULT_PAGE_SIZE, type: 'number' },
  offset: { param: 'offset', default: 0, type: 'number' },
};

// Поля именно фильтра, без limit/offset — см. useAppliedFilters: identity
// appliedFilters не должна меняться от пагинации или чужих параметров типа ruleId.
const pickRulesFilters = (url) => ({
  hierarchy: pickHierarchy(url),
  enabled: url.enabled,
  category: url.category,
  includeDeleted: url.includeDeleted,
});

/*
 * Part 4, раздел 3.4: список, карточка и lifecycle правил живут на V2.
 *
 * Ответ lifecycle — конверт { rule, outcome, resolved_event_ids }; строка
 * обновляется полем rule, а не всем конвертом.
 *
 * Редактирование core-полей (ТЗ 1, 2) идёт через гейт в ViolationRuleViewDrawer
 * (деактивировать → перечитать → только тогда открыть форму): onEdit отдаёт
 * сюда уже свежее правило, а после сохранения список перечитывается заново —
 * ответ backend остаётся источником истины, а не то, что мы отправили.
 */

const RULE_COLUMN_KEYS = [
  'name', 'camera', 'zone', 'condition', 'duration', 'recurrence', 'boost', 'cooldown', 'state', 'mode', 'actions',
];

// Колонка «Категория» — сразу после названия (ТЗ BM v2 §6.1), только когда
// backend отдаёт категории правил.
const RULE_COLUMN_KEYS_WITH_CATEGORY = ['name', 'category', ...RULE_COLUMN_KEYS.slice(1)];

const ViolationRulesTab = ({
  onCreateRule,
  refreshToken = 0,
  initialRuleId = '',
  onRuleIdChange,
  catalogRefreshToken = 0,
  // ТЗ BM v2 §1: форма изменения адресуема — drawer=edit&rule_id=… в URL.
  // editRuleId === undefined — URL не управляет формой (вкладка вне страницы).
  editRuleId,
  onEditRuleChange,
}) => {
  // FE-FILTER02: применённые фильтры и страница живут в URL.
  const [url, setUrl] = useUrlState(RULES_URL_SCHEMA);
  const [appliedFilters, draftFilters, setDraftFilters] = useAppliedFilters(url, pickRulesFilters);
  const { limit, offset } = url;
  const categoriesSupported = useRuleCategoriesSupported();
  const columnKeys = categoriesSupported ? RULE_COLUMN_KEYS_WITH_CATEGORY : RULE_COLUMN_KEYS;

  const [items, setItems] = useState([]);
  const resize = useResizableColumns('violations_rules_widths');
  const [total, setTotal] = useState(0);
  const [listState, setListState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');

  // FE-FIX08: actionNotice хранит только успех; actionError остаётся на экране.
  useAutoDismiss(actionNotice, setActionNotice);
  const [busyRuleIds, setBusyRuleIds] = useState(new Set());
  // Карточка правила держит свой id в URL (ViolationsPage): открытие/закрытие
  // здесь и снаружи должны быть синхронизированы одним источником истины.
  const [viewRuleId, setViewRuleIdState] = useState(initialRuleId);
  const [editRule, setEditRule] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);
  // FE-FIX07: сохранённое расписание меняет режим правила — кэш режимов
  // нужно сбросить, хотя сам список правил перечитывать незачем.
  const [activationToken, setActivationToken] = useState(0);

  const setViewRuleId = (id) => {
    setViewRuleIdState(id);
    onRuleIdChange?.(id);
  };

  // DEP-LINK01: ссылка вида /violations?tab=rules&ruleId=… (из ZoneEditor,
  // DetectionCapabilityRow и т.д.) меняет initialRuleId в уже смонтированном
  // ViolationRulesTab — useState-инициализатор эту смену не подхватывает,
  // карточка не открывалась. И наоборот: ruleId, стёртый из URL снаружи,
  // должен закрыть уже открытую карточку.
  useEffect(() => { setViewRuleIdState(initialRuleId); }, [initialRuleId]);

  // Открытие/закрытие формы и карточки одним изменением URL: два отдельных
  // setSearchParams подряд перетёрли бы друг друга.
  const showEdit = (rule, nextViewRuleId = '') => {
    setEditRule(rule);
    setViewRuleIdState(nextViewRuleId);
    if (onEditRuleChange) onEditRuleChange(rule?.rule_id || '', nextViewRuleId);
    else onRuleIdChange?.(nextViewRuleId);
  };

  // Ссылка drawer=edit&rule_id=… (в том числе после Reload): правило
  // перечитывается с backend, форма открывается только для неактивного.
  // Активное правило открывается карточкой: изменить его можно только после
  // деактивации (ТЗ BM v2 §7).
  const editRuleRef = useRef(editRule);
  editRuleRef.current = editRule;
  const onEditRuleChangeRef = useRef(onEditRuleChange);
  onEditRuleChangeRef.current = onEditRuleChange;
  useEffect(() => {
    if (editRuleId === undefined) return undefined;
    if (!editRuleId) {
      setEditRule(null);
      return undefined;
    }
    if (editRuleRef.current?.rule_id === editRuleId) return undefined;
    let cancelled = false;
    getViolationRuleV2(editRuleId)
      .then((rule) => {
        if (cancelled) return;
        const state = deriveRuleState(rule);
        if (state === RULE_STATE.ACTIVE || state === RULE_STATE.DELETED) {
          setActionError(state === RULE_STATE.ACTIVE
            ? 'Активное правило нельзя изменить. Сначала деактивируйте его.'
            : 'Удалённое правило изменить нельзя.');
          setViewRuleIdState(editRuleId);
          onEditRuleChangeRef.current?.('', editRuleId);
          return;
        }
        setEditRule(rule);
      })
      .catch((error) => {
        if (cancelled) return;
        setActionError(describeUserError(error, 'Не удалось открыть правило для изменения'));
        onEditRuleChangeRef.current?.('', '');
      });
    return () => { cancelled = true; };
  }, [editRuleId]);

  const loadRules = useCallback(() => {
    setListState('loading');

    return listViolationRulesV2({
      ...hierarchyParams(appliedFilters.hierarchy),
      enabled: appliedFilters.enabled || undefined,
      // Без поддержки категорий или без выбора параметр не отправляется:
      // старый backend его не знает, а «все» — это отсутствие фильтра.
      category: categoriesSupported && appliedFilters.category ? appliedFilters.category : undefined,
      include_deleted: appliedFilters.includeDeleted || undefined,
      limit,
      offset,
    })
      .then((response) => {
        setItems(response.items);
        setTotal(response.total);
        setListState('ready');
      })
      .catch((error) => {
        setItems([]);
        setTotal(0);
        setErrorMessage(describeUserError(error, 'Не удалось загрузить список правил'));
        setListState('error');
      });
  }, [appliedFilters, categoriesSupported, limit, offset]);

  useEffect(() => { loadRules(); }, [loadRules, refreshToken]);

  // Finding 5: общий кэш имён камер вместо copy-paste Map+cancelled-эффекта.
  const { cameraLabel } = useCameraNames(items);

  // Finding 4: V2 не отдаёт zone_display_name — имя зоны дотягивается своим
  // запросом на камеру (только для правил с реальным zone_id) и кэшируется
  // по camera_id тем же паттерном, что и имена камер.
  // FE-FIX29: имя нужно и для правил, у которых заполнен только zone_ids —
  // legacy-проверки одного zone_id для этого больше недостаточно.
  const zoneCameraIds = useMemo(
    () => items
      .filter((item) => item.zone_id != null || (item.zone_ids || []).length > 0)
      .map((item) => item.camera_id),
    [items]
  );
  const { zonesByCamera } = useZonesByCamera(zoneCameraIds);
  const { catalogsByCamera } = useRuleCatalogs(items.map((item) => item.camera_id));

  // FE-FIX07: режим активации — отдельный facade-запрос на правило,
  // грузим только для видимой страницы.
  const pageRuleIds = useMemo(() => items.map((rule) => rule.rule_id), [items]);
  const { modeLabel, failed: activationFailed } = useRuleActivationModes(pageRuleIds, {
    refreshToken: `${refreshToken}:${activationToken}`,
  });

  // Sampling Boost в таблице только для просмотра; после сохранения формы
  // кэш сбрасывается, чтобы ячейка показала новое значение.
  const [boostToken, setBoostToken] = useState(0);
  const { boostLabel } = useRuleBoosts(pageRuleIds, { refreshToken: `${refreshToken}:${boostToken}` });

  const filtersAreEmpty = useMemo(
    () => JSON.stringify(appliedFilters) === JSON.stringify(EMPTY_FILTERS),
    [appliedFilters]
  );

  const applyFilters = () => {
    // push, а не replace: применённые фильтры — самостоятельный шаг истории.
    setUrl({
      ...draftFilters.hierarchy,
      enabled: draftFilters.enabled,
      category: draftFilters.category,
      includeDeleted: draftFilters.includeDeleted,
      offset: 0,
    }, { replace: false });
  };
  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    // Сброс — replace по умолчанию, отдельного шага истории не создаёт.
    setUrl({ ...EMPTY_HIERARCHY, enabled: '', category: '', includeDeleted: false, offset: 0 });
  };

  /**
   * После lifecycle-действия страница не перезагружается — строка обновляется
   * ответом бэкенда. Ответ, а не то, что мы отправили, является источником
   * истины: правило могло измениться и другими путями.
   *
   * Part 4, раздел 3.4: ответ — конверт { rule, outcome, resolved_event_ids }.
   * В строку идёт только rule; если бэкенд правило не вернул, перечитываем
   * список, а не дорисовываем состояние сами.
   *
   * busyRuleIds — множество идентификаторов, по которым идут запросы;
   * защищает от рассинхронизации кнопок при параллельных действиях на разных строках.
   */
  const runLifecycle = (rule, action, fallbackMessage) => {
    setActionError('');
    setActionNotice('');
    setBusyRuleIds((prev) => new Set(prev).add(rule.rule_id));

    return action(rule.rule_id)
      .then(({ rule: updated }) => {
        if (!updated) return loadRules();

        setItems((prev) => prev.map((item) => (
          item.rule_id === rule.rule_id ? { ...item, ...updated } : item
        )));
        return undefined;
      })
      .catch((error) => {
        // FE-FIX29: активация правила с удалённой зоной — детерминированный
        // отказ backend. Показываем действие («исправьте зоны правила»), а не
        // общий текст ошибки, и ничего не имитируем как успех.
        if (isZoneUnavailableError(error)) {
          setActionError(ZONE_UNAVAILABLE_MESSAGE);
          return;
        }
        const conflict = describeConflict(error, 'rule_core');
        setActionError(conflict.kind === 'not_conflict'
          ? describeUserError(error, fallbackMessage)
          : conflict.message);
      })
      .finally(() => {
        setBusyRuleIds((prev) => {
          const next = new Set(prev);
          next.delete(rule.rule_id);
          return next;
        });
      });
  };

  const handleActivate = (rule) =>
    runLifecycle(rule, activateViolationRule, 'Не удалось активировать правило');

  const handleDeactivate = (rule) =>
    runLifecycle(rule, deactivateViolationRule, 'Не удалось деактивировать правило');

  const handleDeleteConfirmed = () => {
    const rule = deleteTarget;
    if (!rule) return;

    setDeleting(true);
    setDeleteError('');
    setActionNotice('');

    deleteViolationRule(rule.rule_id)
      .then(() => {
        setDeleteTarget(null);
        if (viewRuleId === rule.rule_id) setViewRuleId('');

        // При include_deleted правило не исчезает, а становится tombstone'ом —
        // его состояние знает только бэкенд, поэтому перечитываем список.
        if (appliedFilters.includeDeleted) return loadRules();

        // Удалённая строка исчезает из обычного списка. Если она была последней
        // на странице, страница уже не существует — перечитываем список.
        if (items.length === 1 && offset > 0) {
          // Системная коррекция страницы, а не шаг пользователя — replace (по умолчанию).
          setUrl({ offset: Math.max(0, offset - limit) });
        } else {
          setItems((prev) => prev.filter((item) => item.rule_id !== rule.rule_id));
          setTotal((prev) => Math.max(0, prev - 1));
        }
        return undefined;
      })
      .catch((error) => {
        setDeleteError(describeUserError(error, 'Не удалось удалить правило'));
      })
      .finally(() => setDeleting(false));
  };

  const renderBody = () => {
    if (listState === 'loading') return <ViolationStateBlock state="loading" />;
    if (listState === 'error') {
      return <ViolationStateBlock state="error" message={errorMessage} onRetry={loadRules} />;
    }
    if (items.length === 0) {
      return filtersAreEmpty ? (
        <ViolationStateBlock state="empty" message="Правил пока нет. Создайте первое правило." />
      ) : (
        <ViolationStateBlock state="empty" message="По выбранным фильтрам правил нет." onReset={resetFilters} />
      );
    }

    return (
      <>
        <div className="vio-table-wrapper">
          <table {...resize.tableProps(columnKeys, 'vio-table vio-rules-table')}>
            <ResizableColgroup resize={resize} keys={columnKeys} />
            <thead>
              <tr>
                <ResizableTh resize={resize} colKey="name">Название</ResizableTh>
                {categoriesSupported && <ResizableTh resize={resize} colKey="category">Категория</ResizableTh>}
                <ResizableTh resize={resize} colKey="camera">Камера</ResizableTh>
                <ResizableTh resize={resize} colKey="zone">Зона</ResizableTh>
                <ResizableTh resize={resize} colKey="condition">Условие</ResizableTh>
                <ResizableTh resize={resize} colKey="duration">Длительность</ResizableTh>
                <ResizableTh resize={resize} colKey="recurrence">Повторяемость</ResizableTh>
                <ResizableTh resize={resize} colKey="boost">Ускорение детекций</ResizableTh>
                <ResizableTh resize={resize} colKey="cooldown">Пауза</ResizableTh>
                <ResizableTh resize={resize} colKey="state">Состояние</ResizableTh>
                <ResizableTh resize={resize} colKey="mode">Режим</ResizableTh>
                <ResizableTh resize={resize} colKey="actions">Действия</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {items.map((rule) => {
                const state = deriveRuleState(rule);
                const busy = busyRuleIds.has(rule.rule_id);
                // Удалённое правило — tombstone: активировать и удалять нечего.
                const deleted = state === RULE_STATE.DELETED;

                return (
                  <tr
                    key={rule.rule_id}
                    className={deleted ? 'vio-row--deleted' : undefined}
                    onClick={() => setViewRuleId(rule.rule_id)}
                  >
                    <td>
                      <span className="vio-rule-name">
                        {rule.name || rule.rule_id}
                        <RuleOriginBadge origin={rule.origin} />
                        <RulePolicyBadges rule={rule} />
                      </span>
                    </td>
                    {categoriesSupported && (
                      <td>
                        <span
                          className={`vio-badge vio-badge--category${
                            ruleCategoryOf(rule) === RULE_CATEGORY_BUSINESS ? ' vio-badge--business' : ''
                          }`}
                        >
                          {ruleCategoryLabel(rule)}
                        </span>
                      </td>
                    )}
                    <td>{cameraLabel(rule.camera_id)}</td>
                    <td>
                      {getZoneScopeLabel({
                        zoneIds: rule.zone_ids,
                        zoneId: rule.zone_id,
                        zoneScope: rule.zone_scope,
                        zones: zonesByCamera[rule.camera_id] || [],
                        fallbackName: rule.zone_display_name,
                      })}
                    </td>
                    <td>{formatRuleCondition(rule, { catalog: catalogsByCamera[rule.camera_id] })}</td>
                    <td>{rule.duration_seconds ?? '—'} сек</td>
                    <td>{formatRecurrenceCompact(rule)}</td>
                    <td>{boostLabel(rule.rule_id)}</td>
                    <td>{formatCooldownShort(rule)}</td>
                    <td><RuleStatusBadge rule={rule} /></td>
                    <td title={activationFailed(rule.rule_id) ? 'Не удалось загрузить расписание правила' : undefined}>
                      {modeLabel(rule.rule_id)}
                    </td>
                    <td>
                      <span className="vio-cell-actions">
                        <button
                          type="button"
                          className="vio-btn vio-btn--link"
                          disabled={busy}
                          onClick={(event) => { event.stopPropagation(); setViewRuleId(rule.rule_id); }}
                        >
                          Открыть
                        </button>

                        {!deleted && (state === RULE_STATE.ACTIVE ? (
                          <button
                            type="button"
                            className="vio-btn vio-btn--link"
                            disabled={busy}
                            onClick={(event) => { event.stopPropagation(); handleDeactivate(rule); }}
                          >
                            {busy ? 'Отключаем…' : 'Деактивировать'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="vio-btn vio-btn--link"
                            disabled={busy}
                            onClick={(event) => { event.stopPropagation(); handleActivate(rule); }}
                          >
                            {busy ? 'Активируем…' : 'Активировать'}
                          </button>
                        ))}

                        {!deleted && (
                          <button
                            type="button"
                            className="vio-btn vio-btn--danger-link"
                            disabled={busy}
                            onClick={(event) => { event.stopPropagation(); setDeleteError(''); setDeleteTarget(rule); }}
                          >
                            Удалить
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <NumberedPagination
          total={total}
          limit={limit}
          offset={offset}
          pageSizes={PAGE_SIZE_OPTIONS}
          onOffsetChange={(next) => setUrl({ offset: next }, { replace: false })}
          onLimitChange={(size) => setUrl({ limit: size, offset: 0 })}
        />
      </>
    );
  };

  return (
    <>
      <div className="vio-card">
        <div className="vio-filters-grid">
          <CameraHierarchyFilter
            value={draftFilters.hierarchy}
            onChange={(hierarchy) => setDraftFilters((prev) => ({ ...prev, hierarchy }))}
          />

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-enabled-filter">Состояние</label>
            <select
              id="vio-enabled-filter"
              value={draftFilters.enabled}
              onChange={(event) => setDraftFilters((prev) => ({ ...prev, enabled: event.target.value }))}
            >
              {ENABLED_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {categoriesSupported && (
            <div className="vio-field">
              <label className="vio-field-label" htmlFor="vio-category-filter">Категория</label>
              <select
                id="vio-category-filter"
                value={draftFilters.category}
                onChange={(event) => setDraftFilters((prev) => ({ ...prev, category: event.target.value }))}
              >
                {CATEGORY_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          <div className="vio-field">
            <span className="vio-field-label">Удалённые правила</span>
            <label className="vio-checkbox">
              <input
                type="checkbox"
                checked={draftFilters.includeDeleted}
                onChange={(event) => setDraftFilters((prev) => ({
                  ...prev, includeDeleted: event.target.checked,
                }))}
              />
              Показывать удалённые
            </label>
          </div>

          <div className="vio-filters-actions">
            <button type="button" className="vio-btn vio-btn--primary" onClick={applyFilters}>Применить</button>
            <button type="button" className="vio-btn" onClick={resetFilters}>Сбросить</button>
          </div>
        </div>
      </div>

      <div className="vio-toolbar">
        <button type="button" className="vio-btn vio-btn--primary" onClick={onCreateRule}>
          <Plus width={16} height={16} /> Создать правило
        </button>
      </div>

      {actionNotice && (
        <div className="vio-card">
          <p className="vio-notice">{actionNotice}</p>
        </div>
      )}

      {actionError && (
        <div className="vio-card">
          <p className="vio-field-error">{actionError}</p>
        </div>
      )}

      {renderBody()}

      {viewRuleId && (() => {
        const viewedRule = items.find((item) => item.rule_id === viewRuleId) || null;
        return (
          <ViolationRuleViewDrawer
            ruleId={viewRuleId}
            ruleOverride={viewedRule}
            cameraLabel={cameraLabel(viewedRule?.camera_id)}
            canEdit
            busy={busyRuleIds.has(viewRuleId)}
            onActivate={viewedRule ? () => handleActivate(viewedRule) : undefined}
            onDeactivate={viewedRule ? () => handleDeactivate(viewedRule) : undefined}
            onDelete={viewedRule ? () => { setDeleteError(''); setDeleteTarget(viewedRule); } : undefined}
            onEdit={(fresh) => showEdit(fresh)}
            onRefetch={loadRules}
            onActivationSaved={() => setActivationToken((token) => token + 1)}
            onClose={() => setViewRuleId('')}
          />
        );
      })()}

      {editRule && (
        <ViolationRuleFormDrawer
          mode="edit"
          rule={editRule}
          cameraLabel={cameraLabel(editRule.camera_id)}
          catalogRefreshToken={catalogRefreshToken}
          onClose={() => showEdit(null)}
          onSaved={(saved) => {
            setBoostToken((token) => token + 1);
            setActionNotice('Правило сохранено и остаётся неактивным. Активируйте его, когда будете готовы.');
            showEdit(null);
            loadRules().then(() => setViewRuleId(saved.rule_id));
          }}
          // Core сохранён, ускорение детекций нет: форма открыта с ошибкой,
          // таблица перечитывается, чтобы показать сохранённые поля.
          onPartiallySaved={() => {
            setBoostToken((token) => token + 1);
            loadRules();
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmRuleDeleteModal
          ruleName={deleteTarget.name || deleteTarget.rule_id}
          busy={deleting}
          error={deleteError}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => { setDeleteTarget(null); setDeleteError(''); }}
        />
      )}
    </>
  );
};

export default ViolationRulesTab;
